import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Medal, Download, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const fmtGiornoTip = (iso) => { try { const s = new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };

const GiorniTip = ({ giorni, children }) => {
  if (!giorni?.length) return children;
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-xs" data-testid="classifica-giorni-tooltip">
        <ol className="text-[11px] leading-snug space-y-0.5">
          {giorni.map((g, i) => <li key={i}>{i + 1}. {fmtGiornoTip(g.data)}{g.turno ? ` · turno ${g.turno}` : ""}</li>)}
        </ol>
      </TooltipContent>
    </Tooltip>
  );
};

const MEDAL_COL = { gold: "#eab308", silver: "#9ca3af", bronze: "#b45309" };
const MEDAL_LABEL = { gold: "Oro", silver: "Argento", bronze: "Bronzo" };

// Categorie "mansioni obbligatorie non svolte" (report automatici) condivise tra
// la pagina Report operatori e il tab Report della scheda operatore.
export const RICHIAMI_CATEGORIE = [
  { key: "Ritardo", label: "Ritardi" },
  { key: "Ingresso non registrato", label: "Ingressi mancanti" },
  { key: "Dashboard non vista", label: "Dashboard non viste" },
  { key: "Consegne non viste", label: "Consegne non viste" },
];

export function ClassificaOperatori({ mese, giorno, showExport = true, extraCategorie = [], extraCounts = {}, extraDays = {}, alwaysShowExtra = false, soloNome = null, onSegnala = null, soloExtra = false }) {
  const { selectedStructure } = useAuth();
  const params = giorno ? { structure_id: selectedStructure, giorno } : { structure_id: selectedStructure, mese };
  const { data, isLoading: loadingMain } = useQuery({
    enabled: !soloExtra,
    queryKey: ["classifica-operatori", selectedStructure, mese, giorno],
    queryFn: async () => (await api.get("/report/operatori", { params })).data,
  });
  const isLoading = !soloExtra && loadingMain;
  const categorie = data?.categorie || [];
  const operatori = data?.operatori || [];
  const { data: allerteData } = useQuery({
    enabled: !giorno && !soloNome && !soloExtra,
    queryKey: ["classifica-allerte", selectedStructure, mese],
    queryFn: async () => (await api.get("/report/operatori/allerte", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const allerte = allerteData?.allerte || [];
  const alertByName = {};
  allerte.forEach((a) => { (alertByName[a.nome] = alertByName[a.nome] || []).push(a); });

  const { data: turniGiorno = [] } = useQuery({
    enabled: !!giorno && !soloExtra,
    queryKey: ["classifica-turni-giorno", selectedStructure, giorno],
    queryFn: async () => (await api.get("/turni", { params: { structure_id: selectedStructure } })).data,
  });
  const turniByNome = useMemo(() => {
    const map = {};
    if (giorno) {
      (turniGiorno || []).filter((t) => t.data === giorno && t.status !== "eliminato").forEach((t) => {
        const nm = (t.operatore_nome || "").trim();
        if (nm) (map[nm] = map[nm] || []).push(t.turno);
      });
    }
    return map;
  }, [turniGiorno, giorno]);
  const TURNO_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte", intermedio: "Intermedio", spezzato: "Spezzato" };

  // Medaglie complessive: top 3 per totale mansioni giuste (calcolate su tutti gli operatori)
  const overallRank = useMemo(() => {
    const withTot = operatori.filter((o) => (o.totale || 0) > 0).sort((a, b) => (b.totale || 0) - (a.totale || 0));
    const map = {};
    if (withTot[0]) map[withTot[0].nome] = "gold";
    if (withTot[1]) map[withTot[1].nome] = "silver";
    if (withTot[2]) map[withTot[2].nome] = "bronze";
    return map;
  }, [operatori]);

  // Righe mostrate: tutte, oppure solo quella dell'operatore corrente
  const rows = useMemo(() => {
    if (!soloNome) return operatori;
    const found = operatori.find((o) => o.nome === soloNome);
    return [found || { nome: soloNome, counts: {}, totale: 0, totale_prec: 0 }];
  }, [operatori, soloNome]);

  const totNonFatte = (o) => extraCategorie.reduce((s, c) => s + ((extraCounts[o.nome] || {})[c.key] || 0), 0);
  // Righe "non svolte": unione tra chi ha mansioni svolte e chi ha mancanze (anche senza svolte).
  const nonSvolteBase = useMemo(() => {
    if (soloNome) return rows;
    const names = Array.from(new Set([...rows.map((r) => r.nome), ...Object.keys(extraCounts || {})]));
    return names.map((nm) => rows.find((r) => r.nome === nm) || { nome: nm, counts: {}, totale: 0, totale_prec: 0 });
  }, [rows, extraCounts, soloNome]);
  const rowsNonSvolte = [...nonSvolteBase].sort((a, b) => totNonFatte(a) - totNonFatte(b) || a.nome.localeCompare(b.nome));
  const hasExtra = extraCategorie.length > 0 && rowsNonSvolte.some((o) => totNonFatte(o) > 0);
  const perc = (o) => {
    const fatte = o.totale || 0;
    const nonFatte = totNonFatte(o);
    if (fatte + nonFatte === 0) return null;
    return Math.round((fatte / (fatte + nonFatte)) * 100);
  };

  const exportCsv = () => {
    if (!operatori.length) return;
    const esc = (val) => { const s = String(val ?? ""); return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ["Operatore", ...categorie.map((c) => c.label), ...extraCategorie.map((c) => c.label), ...(extraCategorie.length ? ["Tot. mansioni non svolte"] : []), "Totale svolte", "% completamento"];
    const lines = [head.map(esc).join(";")];
    operatori.forEach((o) => {
      const tm = totNonFatte(o);
      const p = perc(o);
      lines.push([o.nome, ...categorie.map((c) => o.counts[c.key] || 0), ...extraCategorie.map((c) => (extraCounts[o.nome] || {})[c.key] || 0), ...(extraCategorie.length ? [tm] : []), o.totale, p == null ? "—" : `${p}%`].map(esc).join(";"));
    });
    const csv = "\uFEFF" + lines.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `report_operatori_${giorno || mese}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const exportPdf = async () => {
    try {
      const res = await api.get("/report/operatori/pdf", { params: giorno ? { structure_id: selectedStructure, giorno } : { structure_id: selectedStructure, mese }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `report_operatori_${giorno || mese}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Errore nell'esportazione PDF"); }
  };

  const NomeCell = ({ o, idx, withMedal, pfx = "" }) => {
    const mr = overallRank[o.nome];
    return (
      <td className="p-3 font-semibold sticky left-0 bg-white group-hover:bg-[#f4f6f5] z-10">
        <span className="inline-flex items-center gap-1.5">
          {withMedal && mr && <Medal size={16} style={{ color: MEDAL_COL[mr] }} title={MEDAL_LABEL[mr]} data-testid={`classifica-medal-${pfx}${idx}`} />}
          {o.nome}
        </span>
        {alertByName[o.nome]?.some((a) => a.tipo === "2mesi") && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white align-middle" data-testid={`classifica-badge-2mesi-${pfx}${idx}`}>sotto standard</span>}
        {alertByName[o.nome]?.some((a) => a.tipo === "3giorni") && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white align-middle" data-testid={`classifica-badge-3giorni-${pfx}${idx}`}>3 turni ultimo</span>}
        {giorno && (
          turniByNome[o.nome]?.length
            ? <div className="text-[11px] font-semibold text-sky-700 mt-0.5" data-testid={`classifica-turno-${pfx}${idx}`}>Turno: {[...new Set(turniByNome[o.nome])].map((t) => TURNO_LABEL[t] || t).join(", ")}</div>
            : <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`classifica-turno-${pfx}${idx}`}>Non in turno</div>
        )}
      </td>
    );
  };

  return (
    <div data-testid="classifica-operatori">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-head font-bold text-base" data-testid="report-operatori-title">Report operatori {giorno ? "del giorno" : "del mese"}</h3>
        {showExport && (
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} disabled={!operatori.length} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-secondary disabled:opacity-40" data-testid="classifica-export-excel"><Download size={15} /> Excel</button>
            <button onClick={exportPdf} disabled={!operatori.length} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-secondary disabled:opacity-40" data-testid="classifica-export-pdf"><FileText size={15} /> PDF</button>
          </div>
        )}
      </div>

      {!giorno && !soloNome && allerte.length > 0 && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 mb-3" data-testid="classifica-allerte">
          <div className="flex items-center gap-2 text-sm font-bold text-red-800 mb-1.5"><AlertTriangle size={16} /> Allerte rendimento operatori</div>
          <ul className="space-y-1">
            {allerte.map((a, i) => (
              <li key={i} className="text-sm flex items-center gap-2 flex-wrap" data-testid={`classifica-allerta-${i}`}>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${a.livello === "critico" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{a.livello === "critico" ? "SOTTO LO STANDARD" : "ATTENZIONE"}</span>
                <span className="font-semibold">{a.nome}</span>
                <span className="text-red-700">— {a.testo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading ? (
        <div className="gr-card p-6"><p className="text-sm text-muted-foreground">Caricamento…</p></div>
      ) : (rows.length === 0 && !hasExtra && !alwaysShowExtra) ? (
        <div className="gr-card p-6"><p className="text-sm text-muted-foreground" data-testid="classifica-empty">Nessuna attività registrata{giorno ? " in questo giorno" : " nel mese"}.</p></div>
      ) : (
        <div className="space-y-5">
          {/* Tabella 1 — Mansioni svolte */}
          {rows.length > 0 && !soloExtra && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5 px-1">Mansioni svolte</div>
            <div className="gr-card p-0 overflow-x-auto" data-testid="classifica-table-svolte">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="text-left p-3 sticky left-0 bg-secondary/60 z-10 min-w-[160px]">Operatore</th>
                    {categorie.map((c) => <th key={c.key} className="p-3 text-center whitespace-nowrap">{c.label}</th>)}
                    <th className="p-3 text-center whitespace-nowrap">Totale{!giorno && <span className="block text-[9px] font-normal normal-case text-muted-foreground">vs mese prec.</span>}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o, idx) => {
                    return (
                      <tr key={o.nome} className="border-t hover:bg-secondary/20 group" data-testid={`classifica-op-row-${idx}`}>
                        <NomeCell o={o} idx={idx} withMedal />
                        {categorie.map((c) => {
                          const v = o.counts[c.key] || 0;
                          return (
                            <td key={c.key} className="p-3 text-center" data-testid={`classifica-cell-${c.key}-${idx}`}>
                              <span className={`tabnum ${v > 0 ? "font-semibold" : "text-muted-foreground"}`}>{v}</span>
                            </td>
                          );
                        })}
                        <td className="p-3 text-center" data-testid={`classifica-tot-${idx}`}>
                          <span className="tabnum font-extrabold text-primary">{o.totale}</span>
                          {!giorno && o.totale_prec != null && (() => {
                            const dlt = o.totale - o.totale_prec;
                            if (dlt === 0) return <span className="text-[10px] text-muted-foreground ml-1" title="Come il mese scorso">=</span>;
                            const up = dlt > 0;
                            return <span className={`text-[10px] font-bold ml-1 ${up ? "text-emerald-600" : "text-red-600"}`} title={`Mese scorso: ${o.totale_prec}`}>{up ? "▲" : "▼"}{Math.abs(dlt)}</span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {!hasExtra && alwaysShowExtra && (
            <div data-testid="classifica-nonsvolte-empty">
              <div className="text-xs font-bold uppercase tracking-wide text-rose-700 mb-1.5 px-1">Mansioni obbligatorie non svolte</div>
              <div className="gr-card p-4 text-sm text-muted-foreground italic">Nessuna mansione obbligatoria non svolta {giorno ? "in questo giorno" : "nel mese"}.</div>
            </div>
          )}
          {/* Tabella 2 — Mansioni obbligatorie non svolte */}
          {hasExtra && (
            <TooltipProvider>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-rose-700 mb-1.5 px-1">Mansioni obbligatorie non svolte</div>
              <div className="gr-card p-0 overflow-x-auto" data-testid="classifica-table-nonsvolte">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-rose-50 text-xs uppercase tracking-wide text-rose-700">
                      <th className="text-left p-3 sticky left-0 bg-rose-50 z-10 min-w-[160px]">Operatore</th>
                      {extraCategorie.map((c) => <th key={c.key} className="p-3 text-center whitespace-nowrap" title="Mansione obbligatoria non svolta">{c.label}</th>)}
                      <th className="p-3 text-center whitespace-nowrap bg-rose-100/70 text-rose-800">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsNonSvolte.map((o, idx) => {
                      const tm = totNonFatte(o);
                      return (
                        <tr key={o.nome} className="border-t hover:bg-rose-50/40 group" data-testid={`classifica-nonsvolte-row-${idx}`}>
                          <NomeCell o={o} idx={idx} withMedal={false} pfx="ns-" />
                          {extraCategorie.map((c) => {
                            const v = (extraCounts[o.nome] || {})[c.key] || 0;
                            return (
                              <td key={c.key} className="p-3 text-center" data-testid={`classifica-extra-${c.key}-${idx}`}>
                                <span className="inline-flex items-center gap-1.5">
                                  <GiorniTip giorni={v > 0 ? ((extraDays[o.nome] || {})[c.key] || []) : []}>
                                    <span className={`tabnum ${v > 0 ? "text-rose-700 font-bold cursor-help underline decoration-dotted underline-offset-2" : "text-muted-foreground"}`} data-testid={`classifica-extra-num-${c.key.replace(/\s+/g, "-")}-${idx}`}>{v}</span>
                                  </GiorniTip>
                                  {onSegnala && v > 0 && (
                                    <button type="button" onClick={() => onSegnala(o.nome, c, v)} title={`Segnala ${o.nome} · ${c.label}`} aria-label="Segnala operatore"
                                      data-testid={`classifica-segnala-${c.key.replace(/\s+/g, "-")}-${idx}`}
                                      className="gr-dot-pulse h-3.5 w-3.5 rounded-full bg-red-600 border border-red-700 shrink-0 cursor-pointer" />
                                  )}
                                </span>
                              </td>
                            );
                          })}
                          <td className="p-3 text-center bg-rose-100/40" data-testid={`classifica-totmansioni-${idx}`}>
                            <span className={`tabnum font-bold ${tm >= 5 ? "text-white bg-rose-600 px-2 py-0.5 rounded-full" : tm > 0 ? "text-rose-700" : "text-muted-foreground"}`}>{tm}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
}
